import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { movies } from '@/mocks/movies';
import { genres, genreNameMap } from '@/mocks/genres';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((item) => item.id !== movie.id && item.genres.some((g) => movie.genres.includes(g)))
      .sort((a, b) => b.heat - a.heat)
      .slice(0, 4);
  }, [movie]);

  usePageMeta({
    title: movie ? `${movie.title} - 草民电影电影详情` : '电影详情 - 草民电影',
    description: movie ? movie.synopsis.slice(0, 100) : '草民电影影片详情页。',
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!movie) {
    return (
      <div className="w-full px-4 py-16 md:px-6">
        <EmptyState
          title="该影片信息暂缺"
          message="你访问的影片可能不存在或链接有误，建议浏览其他热门影片。"
          icon="ri-movie-2-line"
          actionLabel="浏览热门电影"
          onAction={() => window.REACT_APP_NAVIGATE('/movies/popular')}
        />
      </div>
    );
  }

  const metaRows = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.cast.join(' / ') },
    { label: '类型', value: movie.genres.map((g) => genreNameMap[g] ?? g).join(' / ') },
    { label: '上映日期', value: movie.releaseDate },
    { label: '片长', value: `${movie.duration} 分钟` },
    { label: '地区', value: movie.region },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-900">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            className="h-full w-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/60" />
        </div>

        <div className="relative z-10 w-full px-4 py-8 md:px-6 md:py-12">
          <div className="[&_a]:text-background-300 [&_span]:text-background-300">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '电影类型', to: '/movies/popular' },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="h-[360px] w-60 shrink-0 overflow-hidden rounded-lg bg-primary-800">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 电影海报`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 font-label text-sm uppercase tracking-widest text-background-300">
                {movie.titleEn}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1.5 text-2xl font-bold text-accent-400">
                  <i className="ri-star-fill text-xl" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-sm text-background-300">{movie.year}</span>
                <span className="text-sm text-background-300">{movie.duration} 分钟</span>
                <span className="text-sm text-background-300">{movie.region}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/movies/category/${g}`}
                    className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-accent-500"
                  >
                    {genreNameMap[g] ?? g}
                  </Link>
                ))}
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200">
                {movie.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-link'} />
                  {copied ? '链接已复制' : '复制影片链接'}
                </button>
                <Link
                  to="/movies/popular"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  浏览更多影片
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[300px_1fr]">
          <aside className="flex flex-col gap-4">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="inline-block h-5 w-1 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            {related.length === 0 ? (
              <p className="rounded-lg border border-dashed border-background-300 bg-background-100 p-6 text-sm text-foreground-500">
                暂无相关内容，敬请期待
              </p>
            ) : (
              related.map((item) => <MovieCard key={item.id} movie={item} />)
            )}
          </aside>

          <div className="min-w-0">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="inline-block h-5 w-1 rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              {movie.synopsis}
            </p>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="inline-block h-5 w-1 rounded-full bg-accent-500" />
              影片信息
            </h2>
            <dl className="mt-4 overflow-hidden rounded-lg border border-background-200">
              {metaRows.map((row, index) => (
                <div
                  key={row.label}
                  className={`flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-6 ${
                    index % 2 === 0 ? 'bg-background-50' : 'bg-background-100'
                  }`}
                >
                  <dt className="w-24 shrink-0 text-sm font-medium text-foreground-500">
                    {row.label}
                  </dt>
                  <dd className="text-sm text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="inline-block h-5 w-1 rounded-full bg-accent-500" />
              所属类型
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {genres.map((genre) => {
                const active = movie.genres.includes(genre.key);
                return (
                  <Link
                    key={genre.key}
                    to={`/movies/category/${genre.key}`}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-accent-100 text-accent-900 hover:bg-accent-200'
                        : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                    }`}
                  >
                    <i className={genre.icon} /> {genre.name}
                  </Link>
                );
              })}
            </div>

            <p className="mt-8 rounded-lg bg-secondary-50 p-5 text-sm leading-relaxed text-secondary-900">
              <i className="ri-information-line mr-1" />
              本站内容为演示性质，仅提供影片资讯与资料展示，不提供任何播放与下载服务。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}